import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import scansApi from "../../API/scans.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import BaseAlertModal from "../Modals/BaseAlertModal";
import ScansReferenceModal from "../ScansSteps/ScansReferenceModal";
import ScansDataHeader from "./ScansDataHeader";
import ScansAPI from "../../API/scans.api";
import { addKeyIfExists, splitValueUnit } from "../../utils/Helpers";
import {
    CreateHistogramPoints,
    CreateSpectrumPoints,
    getColumn,
} from "../../utils/CreatePoints";
import { isEmpty, isNaN } from "lodash";
import ScansDataTable from "../Tables/ScansDataTable";
import SpectrumChart from "../Charts/SpectrumChart";
import HistogramChart from "../Charts/HistogramChart";
// import SEPCard from "../Cards/SEPCard";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { getObject } from "../../utils/StorageHelpers";
import NoResultsView from "../Shared/NoResultsView";
import { DATA_TYPES, SCANS_METADATA } from "../../utils/constants";
import {
    formatToWaveLength,
    formatToAbsorbance,
} from "../../utils/SpectraDataHelper";
import { X_AXIS_TYPES, Y_AXIS_TYPES } from "../../utils/constants";

const ManageScansData = ({
    appliedFilter,
    scans,
    setScans,
    getIsOutlierValue,
    lotsLookup,
    suppliersLookup,
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [deletedPoints, setDeletedPoints] = useState([]);
    const [, setSEPparameters] = useState([]);
    const [spectrumPoints, setSpectrumPoints] = useState([]);
    const [selectedSpectrumPoints, setSelectedSpectrumPoints] = useState([]);
    const [histogramPoints, setHistogramPoints] = useState([]);
    const [, setExternalHistogramPoints] = useState([]);
    const [dataReference] = useState({
        headers: [],
        rows: [],
    });
    const [externalDataReference, setExternalDataReference] = useState({
        headers: [],
        rows: [],
    });
    const [referenceProgressBarValue, setReferenceProgressBarValue] =
        useState(0);
    const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
    const [includeReferencesModelVisible, setIncludeReferencesModelVisible] =
        useState(false);
    const [isWavenumber, setIsWavenumber] = useState(true);
    const [isReflectance, setIsReflectance] = useState(true);
    const [xAxisCheckedReference, setXAxisCheckedReference] = useState({
        value: X_AXIS_TYPES.waveNumber,
    });
    const [yAxisCheckedReference, setYAxisCheckedReference] = useState({
        value: Y_AXIS_TYPES.reflectance,
    });
    const [data, setData] = useState(null);
    const [modifiedDataReference, setModifiedDataReference] = useState({
        headers: [],
        rows: [],
    });
    const [dataReferenceFileName, setDataReferenceFileName] = useState("");

    const tabs = [
        { name: t("data"), allowNoData: true },
        { name: t("spectrum"), allowNoData: true },
        { name: t("histogram"), allowNoData: true },
        // { name: t("externalHistogram"), allowNoData: false, isExternal: true },
        // { name: t("sepError"), allowNoData: false, isExternal: true },
    ];

    useEffect(() => {
        if (data && data.rows) {
            setSpectrumPoints(CreateSpectrumPoints(data, true));
        }
    }, [data]);

    useEffect(() => {
        if (modifiedDataReference) {
            setHistogramPoints(CreateHistogramPoints(modifiedDataReference));
        }
    }, [modifiedDataReference]);

    useEffect(() => {
        if (externalDataReference) {
            setExternalHistogramPoints(
                CreateHistogramPoints(externalDataReference)
            );
            handleSEPCalculation(externalDataReference);
        }
    }, [externalDataReference]);

    useEffect(() => {
        if (!isEmpty(appliedFilter))
            getScans(0, getObject("Scans-itemsPerPage", true) || 100);
    }, [appliedFilter]);

    useEffect(() => {
        if (!isEmpty(data?.headers)) {
            formatToWaveLength(setData);
        }
    }, [isWavenumber]);

    useEffect(() => {
        if (!isEmpty(data?.rows)) {
            formatToAbsorbance(isReflectance, setData);
        }
    }, [isReflectance]);

    const handleReset = () => {
        const emptyState = {
            headers: [],
            rows: [],
        };
        setSelectedRows([]);
        setDeletedPoints([]);
        setExternalDataReference({ ...emptyState });
        setModifiedDataReference({ ...emptyState });
        setData(null);
        setSpectrumPoints([]);
        setSelectedSpectrumPoints([]);
        setHistogramPoints([]);
        setExternalHistogramPoints([]);
        setReferenceProgressBarValue(0);
        setSEPparameters([]);
    };

    const markAsMeasurements = (includePredictions = true) => {
        return new Promise((resolve, reject) => {
            try {
                const scanDetails = { ...data.scanDetails };

                let selectedIds = selectedRows.map((rowId) => {
                    const id = rowId.split("_")[0];
                    scanDetails[id].isMeasurement = true;
                    return id;
                });

                scansApi
                    .markScansAsMeasurement(selectedIds, includePredictions)
                    .then(() => {
                        SuccessAlert(t("markedAsMeasurements"));
                        LogCapture.info(
                            `Scan marked as measurement for selected ids: ${selectedIds}`
                        );

                        setData({ ...data, scanDetails });
                        resolve();
                    })
                    .catch((err) => {
                        ErrorAlert(err);
                        LogCapture.error(
                            `Couldn't mark scan as measurement for selected ids: ${selectedIds}, error: ${err}`
                        );
                        reject();
                    });
            } catch (error) {
                reject(error);
            }
        });
    };

    const getCloudData = async (page, itemsPerPage) => {
        setIsLoading(true);
        const result = await ScansAPI.getScansWithDetails(
            itemsPerPage || 2,
            mapScanFilter(),
            page || 0
        );

        setIsLoading(false);
        setScans(!isEmpty(result) ? result : {});
        return !isEmpty(result) ? result : {};
    };

    const mapScanFilter = () => {
        const thresholdSample = appliedFilter?.threshold || {};
        thresholdSample.filters = thresholdSample?.filters
            ?.filter((filter) => {
                return (
                    filter?.target &&
                    filter?.compare &&
                    filter?.value &&
                    filter?.unit
                );
            })
            .map((filter) => ({
                ...filter,
                ...(filter.unit?.displayUnit && {
                    unit: filter.unit.displayUnit,
                }),
                value: filter.value,
            }));

        return {
            materialId: appliedFilter?.material?.value,
            ...(appliedFilter?.capturedStartDate
                ? { capturedStartDate: appliedFilter?.capturedStartDate }
                : {}),
            ...(appliedFilter?.capturedEndDate
                ? { capturedEndDate: appliedFilter?.capturedEndDate }
                : {}),
            models: getPropertyValue(appliedFilter?.models),
            lots: getPropertyValue(appliedFilter?.lots),
            createdByUsers: getPropertyValue(appliedFilter?.createdByUsers),
            onBehalfOf: getPropertyValue(appliedFilter?.onBehalfOf),
            devices: getPropertyValue(appliedFilter?.devices),
            suppliers: !isEmpty(appliedFilter?.suppliers)
                ? appliedFilter?.suppliers?.map((supplier) => supplier.value)
                : [],
            ...(!isEmpty(thresholdSample?.filters) && {
                threshold: thresholdSample,
            }),
            ...(appliedFilter?.sampleName
                ? { sampleName: appliedFilter?.sampleName }
                : {}),
            ...(appliedFilter?.note ? { note: appliedFilter?.note } : {}),
            isOutlier: getIsOutlierValue(appliedFilter?.isOutlier),
        };
    };

    const getPropertyValue = (arr, propValue = "value") =>
        !isEmpty(arr) ? arr.map((item) => item[propValue]) : [];

    const getScans = async (page, itemsPerPage) => {
        const totalData = await getCloudData(page, itemsPerPage);
        let headers = totalData?.headers;
        const predictionHeaders =
            totalData.predictionHeaders &&
            Object.keys(totalData.predictionHeaders).sort();

        let rows = [],
            metadata = {},
            ids = [],
            scanDetails = {},
            predictionRows = [];
        if (!totalData.scans) {
            handleReset();
            return;
        }
        for (const scan of totalData.scans) {
            const dataRow = [scan.name, ...(scan.averageSpectra || [])];
            rows.push(dataRow);
            ids.push({ id: scan.id });
            for (const m of Object.values(SCANS_METADATA)) {
                addKeyIfExists(scan, m, metadata);
            }

            // Add predictions
            let predictionRow = predictionHeaders?.map((header) => {
                let prediction = "--";
                if (scan.predictions[header]) {
                    if (scan.predictions[header]?.value) {
                        prediction = scan.predictions[header].value;
                    } else {
                        prediction = t("invalid");
                    }
                }
                return prediction;
            });

            predictionRows.push(predictionRow);
            scanDetails[scan.id] = scan;
        }

        headers = !isWavenumber
            ? [...headers.map((wn) => 10 ** 7 / wn)]
            : headers;
        rows = !isReflectance
            ? rows.map((row) =>
                  row.map((elem) =>
                      typeof elem === DATA_TYPES.number
                          ? -Math.log10(+elem / 100)
                          : elem
                  )
              )
            : rows;
        setData({ headers, rows, metadata, ids, scanDetails });

        setModifiedDataReference({
            headers: predictionHeaders,
            rows: predictionRows,
        });
        setIsLoading(false);
        return {
            headers,
            rows,
            metadata,
            ids,
            scanDetails,
        };
    };

    const handleSEPCalculation = (externalReference) => {
        let parameter,
            index,
            refVector = [],
            externalRefVector = [],
            count = 0,
            average,
            parameters = [];

        for (let i = 0; i < externalReference.headers.length; i++) {
            parameter = modifiedDataReference.headers[i];
            index = externalReference.headers.findIndex(
                (elem) => elem.toLowerCase() === parameter.toLowerCase()
            );

            if (index > -1) {
                refVector = getColumn(modifiedDataReference.rows, i);
                externalRefVector = getColumn(externalReference.rows, index);

                average = refVector.reduce((accumulator, element) => {
                    const splittedElement = splitValueUnit(element);
                    const splittedRefValue = splitValueUnit(
                        externalRefVector[count++]
                    );
                    const refNumber = +splittedRefValue;
                    return (
                        accumulator +
                        Math.abs(splittedElement - !isNaN(refNumber) || 0)
                    );
                }, 0);

                parameters.push({
                    name: parameter,
                    value: average / refVector.length,
                });
                count = 0;
            }
        }

        setSEPparameters(parameters);
    };

    const isExternalTab = (tabExternal) => {
        return tabExternal && externalDataReference.rows.length <= 0;
    };

    const renderTabs = () => {
        return (
            tabs &&
            tabs.map((tab, index) => {
                return (
                    <div
                        key={index}
                        onClick={(event) => {
                            if (isExternalTab(tab.isExternal)) {
                                event.preventDefault();
                                return;
                            }
                            if (tab.allowNoData || data) {
                                setActiveTab(index);
                            }
                        }}
                        className={`tabs-bnt-item ${
                            isExternalTab(tab.isExternal) ? "disabled" : ""
                        } ${activeTab === index ? "active" : ""}`}
                        data-tabs="data-content-wrapper"
                    >
                        {tab.name}
                    </div>
                );
            })
        );
    };

    if (isEmpty(scans) && !isLoading) {
        if (!isEmpty(appliedFilter)) return <NoResultsView />;
        return null;
    }

    return (
        <>
            <ScansDataHeader
                selectedRows={selectedRows}
                data={data}
                setIncludeReferencesModelVisible={
                    setIncludeReferencesModelVisible
                }
                setIsReferenceModalOpen={setIsReferenceModalOpen}
                isReferenceModalOpen={isReferenceModalOpen}
                dataReferenceFileName={dataReferenceFileName}
                referenceProgressBarValue={referenceProgressBarValue}
            />
            <div className="tabs-wrapper">
                <div className="tabs-btn">{renderTabs()}</div>
                <div className="tabs-content">
                    <ScansDataTable
                        visible={activeTab === 0}
                        data={data}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        selectedSpectrumPoints={selectedSpectrumPoints}
                        setSelectedSpectrumPoints={setSelectedSpectrumPoints}
                        modifiedDataReference={modifiedDataReference}
                        setModifiedDataReference={setModifiedDataReference}
                        externalDataReference={externalDataReference}
                        setExternalDataReference={setExternalDataReference}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isWavenumber={isWavenumber}
                        setIsWavenumber={setIsWavenumber}
                        isReflectance={isReflectance}
                        setIsReflectance={setIsReflectance}
                        xAxisCheckedReference={xAxisCheckedReference}
                        setXAxisCheckedReference={setXAxisCheckedReference}
                        yAxisCheckedReference={yAxisCheckedReference}
                        setYAxisCheckedReference={setYAxisCheckedReference}
                        scans={scans}
                        getScans={getScans}
                        mapScanFilter={mapScanFilter}
                        appliedFilter={appliedFilter}
                        suppliersLookup={suppliersLookup}
                        lotsLookup={lotsLookup}
                        materialId={appliedFilter?.material?.value}
                    />

                    <SpectrumChart
                        visible={activeTab === 1}
                        points={spectrumPoints}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        deletedPoints={deletedPoints}
                        setDeletedPoints={setDeletedPoints}
                        isWavenumber={isWavenumber}
                        setIsWavenumber={setIsWavenumber}
                        isReflectance={isReflectance}
                        setIsReflectance={setIsReflectance}
                        tooltip={{
                            headerFormat: "<b>{series.name}</b>: {point.y}<br>",
                            pointFormat: "",
                        }}
                        setXAxisCheckedReference={setXAxisCheckedReference}
                        setYAxisCheckedReference={setYAxisCheckedReference}
                    />

                    <HistogramChart
                        visible={activeTab === 2}
                        points={histogramPoints}
                    />

                    {/* <HistogramChart
                        visible={activeTab === 3}
                        points={externalHistogramPoints}
                    />

                    <SEPCard
                        visible={activeTab === 4}
                        parameters={SEPparameters}
                    /> */}
                </div>
            </div>
            <ScansReferenceModal
                modalButton={isReferenceModalOpen}
                modalButtonSetter={setIsReferenceModalOpen}
                dataReference={dataReference}
                data={data}
                // setReferenceData={setDataReference}
                setDataReferenceFileName={setDataReferenceFileName}
                setReferenceProgressBarValue={setReferenceProgressBarValue}
                predictionHeaders={modifiedDataReference.headers}
                setExternalDataReference={setExternalDataReference}
            />

            <BaseAlertModal
                isVisible={includeReferencesModelVisible}
                setIsVisible={setIncludeReferencesModelVisible}
                title={t("markAsMeasurementsTitle")}
                message={t("markAsMeasurementsMessage")}
                action={{
                    submitAction: markAsMeasurements,
                }}
                type="confirmation"
            />
        </>
    );
};

export default ManageScansData;
